import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Globe,
  Loader2,
  MessageCircle,
  MessageSquarePlus,
  UserX,
} from 'lucide-react';
import './ArticleReader.css';

type ArticleData = {
  thread: {
    id: number;
    title: string;
    visibility: 'public' | 'anonymous';
    questionCount: number;
    reportMarkdown: string | null;
    publishedAt: string | null;
  };
  author: {
    id: number | null;
    email: string | null;
    displayName: string | null;
  };
  sources: Array<{ url: string; title: string; domain: string }>;
  tags: string[];
};

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

type Props = {
  threadId: number | null;
  onClose: () => void;
  onStartThread: (sourceTitle: string, sourceThreadId: number) => void;
  onAskResearch: (threadId: number, title: string) => void;
  onOpenThread?: (id: number) => void;
};

export default function ArticleReader({
  threadId,
  onClose,
  onStartThread,
  onAskResearch,
}: Props) {
  const [data, setData] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await fetch(`/api/discover/${id}`, {
        credentials: 'include',
      });
      const json = (await res.json()) as ArticleData & {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !json.ok) {
        throw new Error(json.error ?? 'Failed to load article');
      }
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (threadId !== null) {
      void load(threadId);
    }
  }, [threadId, load]);

  // Lock body scroll when open
  useEffect(() => {
    if (threadId === null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [threadId]);

  // Escape to close
  useEffect(() => {
    if (threadId === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [threadId, onClose]);

  const open = threadId !== null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="article-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            className="article-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Article reader"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
          >
            {/* ── Top bar with back button ────────────────── */}
            <div className="article-sheet__topbar">
              <button
                className="article-sheet__back"
                onClick={onClose}
                aria-label="Back to home"
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <span className="article-sheet__topbar-title">
                {data?.thread.title ?? ''}
              </span>
            </div>

            {/* ── Body ─────────────────────────────────────── */}
            <div className="article-sheet__body">
              {loading && (
                <div className="article-sheet__state">
                  <Loader2 size={14} className="article-sheet__spin" />{' '}
                  loading...
                </div>
              )}

              {error && <div className="article-sheet__error">{error}</div>}

              {data && (
                <>
                  {/* Title + meta */}
                  <header className="article-sheet__header">
                    <h1 className="article-sheet__title">
                      {data.thread.title}
                    </h1>
                    <div className="article-sheet__meta">
                      {data.thread.visibility === 'public' ? (
                        <span className="article-sheet__author">
                          <Globe size={11} />
                          {data.author.displayName || data.author.email}
                        </span>
                      ) : (
                        <span className="article-sheet__author article-sheet__author--anon">
                          <UserX size={11} />
                          anonymous
                        </span>
                      )}
                      <span className="article-sheet__dot">&middot;</span>
                      <span>{formatDate(data.thread.publishedAt)}</span>
                      <span className="article-sheet__dot">&middot;</span>
                      <span>{data.thread.questionCount} questions</span>
                    </div>
                  </header>

                  {/* Tags */}
                  {data.tags.length > 0 && (
                    <div className="article-sheet__tags">
                      {data.tags.map((tag) => (
                        <span key={tag} className="article-sheet__tag">#{tag}</span>
                      ))}
                    </div>
                  )}

                  {/* Report content */}
                  {data.thread.reportMarkdown ? (
                    <article className="article-sheet__content">
                      <ReactMarkdown>{data.thread.reportMarkdown}</ReactMarkdown>
                    </article>
                  ) : (
                    <div className="article-sheet__no-report">
                      This thread has no report yet.
                    </div>
                  )}

                  {/* Sources */}
                  {data.sources.length > 0 && (
                    <section className="article-sheet__sources">
                      <h3 className="article-sheet__section-title">SOURCES</h3>
                      <ul className="article-sheet__source-list">
                        {data.sources.map((s, i) => (
                          <li key={i}>
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="article-sheet__source-link"
                            >
                              <span className="article-sheet__source-domain">
                                {s.domain}
                              </span>
                              <span className="article-sheet__source-title">
                                {s.title}
                              </span>
                              <ExternalLink size={10} />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {/* Ask this research — opens main chat bar in research mode */}
                  {data.thread.reportMarkdown && (
                    <section className="pc">
                      <button
                        className="pc__trigger"
                        onClick={() => onAskResearch(data.thread.id, data.thread.title)}
                      >
                        <MessageCircle size={13} />
                        <span>ask this research</span>
                        <ArrowRight size={11} className="pc__trigger-arrow" />
                      </button>
                    </section>
                  )}

                  {/* Start new thread CTA */}
                  <section className="article-sheet__cta">
                    <button
                      className="article-sheet__new-thread"
                      onClick={() => {
                        onStartThread(data.thread.title, data.thread.id);
                        onClose();
                      }}
                    >
                      <MessageSquarePlus size={15} />
                      <span>start a deep dive from this insight</span>
                    </button>
                    <p className="article-sheet__cta-sub">
                      go 20 questions deep with AI — your research becomes a
                      publishable draft
                    </p>
                  </section>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
